/* eslint-disable no-use-before-define */
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
} from 'lucide-react';

import { icons } from 'lucide-react';

import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import MarketingBox from '../../../components/MarketingBox.js'
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import useSideNav from 'src/hooks/useSideNav';
import { makeStyles } from 'tss-react/mui';
import { useFlag } from '@unleash/proxy-client-react';
import GetPendingRequests from 'src/api/GetPendingRequests';
import usePendingRequests from 'src/hooks/usePendingRequests';
import { useSnackbar } from 'notistack';
import GetGrantedSubAccounts from 'src/api/GetGrantedSubAccounts'
import GetSubAccountAlerts from 'src/api/GetSubAccountAlerts';
import useSelectedCoin from 'src/hooks/useSelectedCoin';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { applySorting, getComparator } from '../../../utils/filterAndSortData';

function renderNavItems({
  items,
  pathname,
  depth = 0,
  translation,
  pendingRequestsLength,
  activeAlertsCount
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) =>
          reduceChildRoutes({
            acc,
            item,
            pathname,
            depth,
            translation,
            pendingRequestsLength,
            activeAlertsCount
          }),
        []
      )}
    </List>
  );
}

const poolHashrateNav = {
  title: 'poolHashrate',
  icon: '',
  href: '/app/admin/pool-statistics/pool-hashrate'
};
const blocksNav = {
  title: 'blocks',
  icon: '',
  href: '/app/admin/pool-statistics/blocks'
};
const poolDailyAvgStatsNav = {
  title: 'poolDailyStatistics',
  icon: '',
  href: '/app/admin/pool-statistics/pool-daily-hashrate'
};
const allSubAccountsNav = {
  title: 'allSubAccounts',
  icon: '',
  href: '/app/admin/all-sub-accounts'
};
const allSubAccountFinancialsNav = {
  title: 'adminSubAccountFinancials',
  icon: '',
  href: '/app/admin/sub-account-financials'
};
const groupUserNav = {
  title: 'adminGroupUser',
  icon: '',
  href: '/app/admin/group-user'
};
const faqNav = { 
  title: 'faqs', 
  icon: '', 
  href: '/app/help/faqs',
  redirect: true 
};
const statusNav = {
  title: 'status',
  icon: '',
  href: '/app/help/status',
  redirect: true
};
const apiDocNav = {
  title: 'apiDocumentation',
  icon: 'FileCode2',
  href: '/app/help/api-documentation',
  redirect: true
};
const tosNav = {
  title: 'termsOfService',
  icon: '',
  href: '/app/help/terms-of-service',
  redirect: true
};
const accountOverviewNav = {
  title: 'accountOverview',
  icon: 'LayoutGrid',
  href: '/app/account-overview'
};
const accountOverviewNavNewIcon = {
  title: 'home',
  icon: 'Home',
  href: '/app/account-overview'
};
const subAccountsNav = {
  title: 'subAccounts',
  icon: 'Network',
  href: '/app/sub-accounts'
};
const userManagementNav = {
  title: 'userManagement',
  icon: 'Users',
  href: '/app/user-management'
};
const financialOverviewNav = {
  title: 'financialOverview',
  icon: 'Wallet',
  href: '/app/financial-overview'
};
const auditLog = {
  title: 'auditLog',
  icon: 'ScrollText',
  href: '/app/audit-log'
};
const calculator = {
  title: 'fppsCalculator',
  icon: 'Calculator',
  href: '/calculator-beta',
  target: '_blank'
};
const approvals = {
  title: 'approvals',
  icon: 'BadgeCheck',
  href: '/app/approvals'
};

const alerts = {
  title: 'alerts',
  icon: 'Bell',
  href: '/app/alerts/active'
};

const poolStatisticsCollapsibleNav = {
  title: 'poolStatistics',
  icon: 'AreaChart',
  href: '/app/admin/pool-statistics',
  items: [poolHashrateNav, poolDailyAvgStatsNav, blocksNav]
};

const managementCollapsibleNav = {
  title: 'management',
  icon: 'Cog',
  href: '/app/admin/management',
  items: [allSubAccountsNav, allSubAccountFinancialsNav, groupUserNav]
};

const reportsOverviewNav = {
  title: 'reports',
  icon: 'Clipboard',
  href: '/app/reports'
};

const helpCollapsibleNav = {
  title: 'help',
  icon: 'HelpCircle',
  href: '/app/help',
  items: [faqNav, statusNav, tosNav]
};

const adminSection = [
  poolStatisticsCollapsibleNav,
  managementCollapsibleNav
];
const supportSection = [apiDocNav, calculator, helpCollapsibleNav];
const ungroupedSection1 = [accountOverviewNav, subAccountsNav];
const ungroupedSection1WithNewIcon = [accountOverviewNavNewIcon, subAccountsNav];
const ungroupedSection2 = [financialOverviewNav, userManagementNav];
const auditLogSection = [auditLog];
const approvalsSection = [approvals];
const alertsSection = [alerts];

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
  translation,
  pendingRequestsLength,
  activeAlertsCount
}) {

  const key = item.title + depth;

  var title = translation(item.title);

  if (item.items) {
    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={title === 'Pool Statistics' ? true : false}
        title={title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          translation: translation
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        target={item.target}
        redirect={item.redirect}
        icon={item.icon}
        info={item.info}
        key={key}
        title={title}
        pendingRequestsLength={pendingRequestsLength}
        activeAlertsCount={activeAlertsCount}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles()(theme => ({
  metalMobileDrawer: {
    width: 230,
    backgroundColor: 'var(--neutral-2)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  },
  logoSmall: {
    width: '75%'
  },
  metalDesktop: {
    width: 230,
    top: 74,
    height: 'calc(100% - 74px)',
    zIndex: 1000,
    backgroundColor: ' var(--uishell-color-surface)'
  },
  searchPaletteContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    marginTop: "56px",
    "& .MuiPaper-root" : {
      width: "100%",
      borderRadius: "12px"
    },
    "& .MuiListItemButton-root:hover":{
      backgroundColor: "#242428",
      marginLeft: "10px",
      paddingLeft: "8px",
      borderRadius: "8px"
    }
  },
  metalContent: {
    backgroundColor: 'rgba(20, 20, 22, 1)'
  },
  queryButton: {
    fontWeight: "400",
    fontSize: "16px",
    display: "flex",
    justifyContent: "space-between",
    textTransform: "capitalize",
    color: "#9999A1",
    border: "1px solid #333339",
    padding: "10px 14px",
    margin: "6px 18px",
    borderRadius: "8px",
  },
  icon: {
    color: '#A3ACA9',
  },
  iconText: {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: "500",
    fontSize: "14px",
    color: '#FBFBFB',
    "& .MuiListItemText-secondary": {
      marginLeft: "8px"
    }
  }
}));

const formSections = (user, fpps, bulkReports) => {
  const sectionWithoutCalculator = supportSection.filter(
    calc => calc.title !== 'fppsCalculator'
  );

  var sections = [];
  if (user.adminRole.adminRead) {
    sections.push({
      subheader: 'Ungrouped',
      items: ungroupedSection1WithNewIcon
    });
    sections.push({
      subheader: 'Ungrouped',
      items: bulkReports ? [reportsOverviewNav, userManagementNav] : ungroupedSection2
    });
    if (user?.isApprover)
      sections.push({
        subheader: 'Ungrouped',
        items: approvalsSection
      });
    sections.push({
      subheader: 'Ungrouped',
      items: auditLogSection
    });
    sections.push({
      subheader: 'Support',
      items: fpps ? supportSection : sectionWithoutCalculator
    });
    sections.push({
      subheader: 'Ungrouped',
      items: alertsSection
    })

    sections.push({
      subheader: 'admin',
      items: adminSection
    });
  } else {
    const ungroupedSection2Items = bulkReports ? [reportsOverviewNav] : [financialOverviewNav];
    const manageUsersPermission = 
      user.groups?.filter(group => !!group.userRole.manageUsers)?.length > 0;
    const viewHashratePermission = user?.groups
    ?.map(g => g.defaultSubAccountRole.viewHashrate)
    ?.filter(hashratePermission => !!hashratePermission)?.length > 0;
    if (manageUsersPermission) {
      ungroupedSection2Items.push(userManagementNav);
    }

    sections.push({
      subheader: 'Ungrouped',
      items: ungroupedSection1
    });
    sections.push({
      subheader: 'Ungrouped',
      items: ungroupedSection2Items
    });

    const viewPaymentPermission =
      user?.groups
        ?.map(g => g.defaultSubAccountRole.viewPayments)
        ?.filter(payPermission => !!payPermission)?.length > 0;

    if (user?.isApprover)
      sections.push({
        subheader: 'Ungrouped',
        items: approvalsSection
      });

    if (viewPaymentPermission) {
      sections.push({
        subheader: 'Ungrouped',
        items: auditLogSection
      });
    }

    sections.push({
      subheader: 'Support',
      items: fpps ? supportSection : sectionWithoutCalculator
    });

    if (viewHashratePermission) {
      sections.push({
        subheader: 'Ungrouped',
        items: alertsSection
      })
    }

  }
  return sections;
};

const NavBar = props => {
  const navigate = useNavigate();
  const { navLoading, pendingRequestsLength, setPendingRequestsLength } = props;
  const fpps = useFlag('fpps');
  const bulkSubAccountExport = useFlag('bulk_sub_account_export');
  const { t } = useTranslation();
  const { classes } = useStyles();
  const location = useLocation();
  const { loggedInUser, getAccessToken } = useAuth();
  const { navOpen, handleSideNavUpdate } = useSideNav();
  var sections = formSections(loggedInUser, fpps, bulkSubAccountExport);
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const isFirefox = typeof InstallTrigger !== 'undefined';
  const { pendingRequests, handlePendingRequestsUpdate, handlePendingRequestsCountUpdate, pendingRequestsCount } = usePendingRequests();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedCoin: coin } = useSelectedCoin();
  const [ os, setOs ] = useState("")
  const [ subAccountAlerts, setSubAccountAlerts ] = useState([]);
  const [ searchPaletteIsOpen, setSearchPaletteIsOpen ] = useState(false)

  const [ globalSearchQuery, setGlobalSearchQuery ] = useState('')
  const [ searchRecents, setSearchRecents ] = useState([])
  const [ searchSubAccounts, setSearchSubAccounts ] = useState([])
  const [ keyPress, setKeyPress] = useState({})
  const [ keyboardSelectIndex, setKeyboardSelectIndex ] = useState('')

  const { selectedGroup } = useSelectedGroup();

  const divRef = useRef();
  const topRef = useRef()

  const scrollToElement = () => {
    if(divRef?.current){
      const {current} = divRef
      if (current !== null){
        current.scrollIntoView({behavior: "smooth"})
      }
    }
  }

  const scrollToTop = () => {
    if(topRef?.current){
      const {current} = topRef
      if (current !== null){
        current.scrollIntoView({behavior: "smooth"})
      }
    }
  }


  const viewHashRatePermission = loggedInUser?.groups
    ?.map(g => g.defaultSubAccountRole.viewHashrate)
    ?.filter(hashratePermission => !!hashratePermission)?.length > 0;

  const handleSearchPaletteClose = () => {
    setKeyboardSelectIndex('')
    setSearchPaletteIsOpen(false)
  }

  const handleGlobalSearchChange = (e) => {
    scrollToTop()
    setKeyboardSelectIndex("")
    setGlobalSearchQuery(e.target.value.toLowerCase().replaceAll(" ", ""))
  }

  const handleArrowUp = () => {
    if(searchPaletteIsOpen){
      if(keyboardSelectIndex !== ''){
        if(keyboardSelectIndex !== ("100") && keyboardSelectIndex !== ("200") && keyboardSelectIndex !== ("300") && keyboardSelectIndex !== ("3010") && keyboardSelectIndex !== ("30100")){
          setKeyboardSelectIndex(prevValue => (parseInt(prevValue) - 1).toString())
        }
        else if(keyboardSelectIndex === "200"){
          if(!globalSearchQuery){
            setKeyboardSelectIndex("10" + (searchRecents.length - 1))
          }
          else {
            if(searchRecents.map(e => JSON.parse(e)).filter(e=>{
              return e.value.toLowerCase().includes(globalSearchQuery)
            }).length){
              setKeyboardSelectIndex("10" + (searchRecents.map(e => JSON.parse(e)).filter(e=>{
                return e.value.toLowerCase().includes(globalSearchQuery)
              }).length - 1))
            }
          }
        }
        else if(keyboardSelectIndex === "300"){
          if(!globalSearchQuery){
            setKeyboardSelectIndex("202")
          }
          else {
            if(sections.flatMap(i => i.items).filter(e=>{
              return e.title.toLowerCase().includes(globalSearchQuery)
            }).length){
              setKeyboardSelectIndex("20" + (sections.flatMap(i => i.items).filter(e=>{
                return e.title.toLowerCase().includes(globalSearchQuery)
              }).length - 1))
            }
            else if(searchRecents.map(e => JSON.parse(e)).filter(e=>{
              return e.value.toLowerCase().includes(globalSearchQuery)
            }).length){
              setKeyboardSelectIndex("10" + (searchRecents.map(e => JSON.parse(e)).filter(e=>{
                return e.value.toLowerCase().includes(globalSearchQuery)
              }).length - 1))
            }
          }
        }
        else if(keyboardSelectIndex === "30100"){
          setKeyboardSelectIndex("3099")
        }
        else if(keyboardSelectIndex === "3010"){
          setKeyboardSelectIndex("309")
        }
      }
    }
  }

  const handleArrowDown = () => {
    if(searchPaletteIsOpen){
      if(keyboardSelectIndex === ''){
        if(searchRecents.map(e => JSON.parse(e)).filter(e=>{
          return e.value.toLowerCase().includes(globalSearchQuery)
        }).length){
          setKeyboardSelectIndex("100")
        }
        else if(sections.flatMap(i => i.items).filter(e=>{
          return e.title.toLowerCase().includes(globalSearchQuery)
        }).length){
          setKeyboardSelectIndex("200")
        }
        else {
          setKeyboardSelectIndex("300")
        }
      }
      else {
        if(keyboardSelectIndex.startsWith("1")){
          if(!!globalSearchQuery){
            if(!!searchRecents.map(e => JSON.parse(e)).filter(e=>{
              return e.value.toLowerCase().includes(globalSearchQuery)
            }).length && keyboardSelectIndex !== ("10" + (searchRecents.map(e => JSON.parse(e)).filter(e=>{
              return e.value.toLowerCase().includes(globalSearchQuery)
            }).length - 1))){
              setKeyboardSelectIndex(prevValue => (parseInt(prevValue) + 1).toString())
            }
            else {
              if(!!sections.flatMap(i => i.items).filter(e=>{
                return e.title.toLowerCase().includes(globalSearchQuery)
              }).length) {
                setKeyboardSelectIndex("200")
              }
              else if(searchSubAccounts.filter(e=>{
                return e.subAccountName.toLowerCase().includes(globalSearchQuery)
              }).length){
                setKeyboardSelectIndex("300")
              }
            }
          }
          else {
            if(keyboardSelectIndex !== ("10" + (searchRecents.length - 1))){
              setKeyboardSelectIndex(prevValue => (parseInt(prevValue) + 1).toString())
            }
            else {
              setKeyboardSelectIndex("200")
            }
          }
        }
        else if(keyboardSelectIndex.startsWith("2")){
          if(!!globalSearchQuery){
            if(keyboardSelectIndex !== ("20" + (sections.flatMap(i => i.items).filter(e=>{
              return e.title.toLowerCase().includes(globalSearchQuery)
            }).length - 1))){
              setKeyboardSelectIndex(prevValue => (parseInt(prevValue) + 1).toString())
            }
            else if(searchSubAccounts.filter(e=>{
              return e.subAccountName.toLowerCase().includes(globalSearchQuery)
            }).length){
              setKeyboardSelectIndex("300")
            }
          }
          else {
            if(keyboardSelectIndex !== "202") {
              setKeyboardSelectIndex(prevValue => (parseInt(prevValue) + 1).toString())
            }
            else {
              setKeyboardSelectIndex("300")
            }
          }
        }
        else if(keyboardSelectIndex.startsWith("3")){
          if(keyboardSelectIndex !== ("30" + (searchSubAccounts.filter(e=>{
              return e.subAccountName.toLowerCase().includes(globalSearchQuery)
            }).length - 1))){
              setKeyboardSelectIndex(prevValue => {
                if((prevValue < 309 || prevValue > 3009) && (prevValue < 3099 || prevValue > 30099)){
                  return (parseInt(prevValue) + 1).toString()
                }else if(prevValue < 3099){
                  return "3010"
                }else {
                  return "30100"
                }
            })
          }
        }
      }
    }
  }

  const handleEnterKey = () => {
    if(searchPaletteIsOpen && (!!globalSearchQuery || keyboardSelectIndex !== '') && (sections.flatMap(i => i.items).filter(e=>{
      return e.title.toLowerCase().includes(globalSearchQuery)
    }).length || searchSubAccounts.filter(e=>{
      return e.subAccountName.toLowerCase().includes(globalSearchQuery)
    }).length)){
      setKeyboardSelectIndex('')
      setSearchPaletteIsOpen(false)
      const localSearchPalette = JSON.parse(localStorage.getItem("searchPalette")) || []
      if(!localSearchPalette.includes([...divRef.current.children][0].dataset.id)){
        if(localSearchPalette.length < 3) {
          localSearchPalette.unshift([...divRef.current.children][0].dataset.id) 
        }
        else {
          localSearchPalette.unshift([...divRef.current.children][0].dataset.id)
          localSearchPalette.pop()
        }
        localStorage.setItem("searchPalette", JSON.stringify(localSearchPalette))
      }
      else {
        localSearchPalette.splice(localSearchPalette.indexOf([...divRef.current.children][0].dataset.id), 1)
        localSearchPalette.unshift([...divRef.current.children][0].dataset.id)
        localStorage.setItem("searchPalette", JSON.stringify(localSearchPalette))
      }
      let data = JSON.parse([...divRef.current.children][0].dataset.id)
      if(data.type === "page"){
        navigate(data.href)
      }
      else {
        navigate(`/app/sub-accounts/${data.value}/dashboard`)
      }
    }
  }

  useEffect(() => {
    const initializeRequests = async () => {
      const { tokenAvailable, accessToken } = await getAccessToken();
      if (!tokenAvailable) {
        console.log(accessToken);
      }
      const { success, data } = await GetPendingRequests(
        accessToken,
        loggedInUser.id
      );
      if (success) {
        handlePendingRequestsUpdate(data);
      } else {
        enqueueSnackbar(data, {
          variant: 'error'
        });
      }
    };
    if (loggedInUser?.isApprover) initializeRequests();

    function detectOS() {
      let platform = navigator?.userAgentData?.platform || navigator?.platform
        //new method navigator.userAgentData but not supported by all browsers i.e. firefox
        //deprecated navigator.platform method but still supported by all browsers
      if (platform.toLowerCase().includes('mac')) {
        return 'macOS';
      } else {
        return 'Windows/Linux/Other';
      }
    }
    let localOs = detectOS();
    const handleKeyDown = (e) => {
      if ((localOs === "macOS" ? e.metaKey : e.ctrlKey) && e.key === "k") {
        setSearchPaletteIsOpen(true)
        e.preventDefault()
      }
      setKeyPress({ key: e.key })
    }
    setOs(localOs)
    document.addEventListener("keydown", handleKeyDown);
    const localSearchPalette = JSON.parse(localStorage.getItem("searchPalette")) || []
    setSearchRecents(localSearchPalette)
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };

  }, []);

  useEffect(() => {
    const getSubAccountAlerts = async () => {
      const { tokenAvailable, accessToken } = await getAccessToken();
      if (!tokenAvailable) {
        console.log(accessToken);
      }
      const { success, data } = await GetSubAccountAlerts(
        accessToken,
        coin,
        'ascSubAccount',
        selectedGroup,
        true
      );
      if (success) {
        setSubAccountAlerts(data)
      } else {
        enqueueSnackbar(data, {
          variant: 'error'
        });
      }
    }
    if (viewHashRatePermission) getSubAccountAlerts();

  }, [coin, selectedGroup])

  useEffect(() => {
    let requestsLength = 0;
    pendingRequests.forEach(
      request => (requestsLength = requestsLength + request.requests.length)
    );
    setPendingRequestsLength(requestsLength);
    handlePendingRequestsCountUpdate(requestsLength)
  }, [pendingRequests]);

  useEffect(() => {
    if (handleSideNavUpdate) {
      handleSideNavUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(()=>{
    if(navOpen){
      setSearchPaletteIsOpen(false)
    }
  }, [navOpen])

  useEffect(()=>{
    switch (keyPress.key) {
      case "ArrowUp":
        handleArrowUp()
        break;
      case "ArrowDown":
        handleArrowDown()
        break;
      case "Enter":
        handleEnterKey()
        break;
      default:
        break;
    }
  }, [keyPress])

  useEffect(scrollToElement, [keyboardSelectIndex])

  const fetchSubAcctData = useCallback(async () => {
    if(searchPaletteIsOpen && !searchSubAccounts.length) {
      const { tokenAvailable, accessToken } = await getAccessToken();
      if (!tokenAvailable) {
        return;
      }
      const { success, data } = await GetGrantedSubAccounts(
        accessToken
      );
      if (success) {
        // Sort the data into a flat map so it's more usable
        let sortedSubAccountData = data.flatMap(item =>
          item.subAccountNamesAndRoles.map(sa => ({
            groupName: item.groupName,
            subAccountName: sa.name
          }))
        )
        const sortedSubAccounts = applySorting(sortedSubAccountData, getComparator('asc', 'subAccountName'))
        setSearchSubAccounts(sortedSubAccounts)
        return
      }
    }
    if(!searchPaletteIsOpen){
      setGlobalSearchQuery('')
      const localSearchPalette = JSON.parse(localStorage.getItem("searchPalette")) || []
      setSearchRecents(localSearchPalette)
    }
  }, [searchPaletteIsOpen])

  useEffect(() => {
    fetchSubAcctData()
  }, [fetchSubAcctData])

  useEffect(()=>{
    if(globalSearchQuery && keyboardSelectIndex === ""){
      if(searchRecents.map(e => JSON.parse(e)).filter(e=>{
        return e.value.toLowerCase().includes(globalSearchQuery)
      }).length){
        setKeyboardSelectIndex("100")
      }
      else if(sections.flatMap(i => i.items).filter(e=>{
        return e.title.toLowerCase().includes(globalSearchQuery)
      }).length){
        setKeyboardSelectIndex("200")
      }
      else if(searchSubAccounts.filter(e=>{
        return e.subAccountName.toLowerCase().includes(globalSearchQuery)
      }).length){
        setKeyboardSelectIndex("300")
      }
    }
  }, [globalSearchQuery])

  const handleSearchClick = (e) => {
    setSearchPaletteIsOpen(false)
    const localSearchPalette = JSON.parse(localStorage.getItem("searchPalette")) || []
    if(!localSearchPalette.includes(e.currentTarget.dataset.id)){
      if(localSearchPalette.length < 3) {
        localSearchPalette.unshift(e.currentTarget.dataset.id) 
      }
      else {
        localSearchPalette.unshift(e.currentTarget.dataset.id)
        localSearchPalette.pop()
      }
      localStorage.setItem("searchPalette", JSON.stringify(localSearchPalette))
    }
    else {
      localSearchPalette.splice(localSearchPalette.indexOf(e.currentTarget.dataset.id), 1)
      localSearchPalette.unshift(e.currentTarget.dataset.id)
      localStorage.setItem("searchPalette", JSON.stringify(localSearchPalette))
    }
  }

  const content = (
    <Box height="100%" display="flex" flexDirection="column" position={"relative"}>
      <PerfectScrollbar style={{display: "flex", flexDirection:"column"}} options={{ suppressScrollX: true }}>
        {mobileDevice && (
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink
              to={
                window.location.host === 'foundryusapool.com'
                  ? 'https://foundrydigital.com/mining-service/foundry-usa-pool/'
                  : '/'
              }
              style={isFirefox ? { width: '100%' } : {}}
            >
              <Logo className={classes.logoSmall} />
            </RouterLink>
          </Box>
        )}
        <Button
          disableRipple
          onClick={()=>setSearchPaletteIsOpen(true)}
          className={classes.queryButton}
          style={{ backgroundColor: 'transparent' }} 
          >
            <SvgIcon fontSize="small" color="action">
              <SearchIcon />
            </SvgIcon>
            <Typography style={{marginLeft: "8px"}}>{t('search')}</Typography>
            <div style={{flex:1}}></div>
            <Typography>{os === "macOS" ? "⌘K" : "ctrl + k"}</Typography>
        </Button>
        {navLoading ? (
          <Box display="flex" justifyContent="center" mt={25}>
            <CircularProgress></CircularProgress>
          </Box>
        ) : (
          <Box pb={2}>
            {sections
              ? sections.map((section, index) => (
                  <List
                    key={index}
                    className={section.subheader.includes('admin') ? classes.adminSection : ""}
                    style={{ paddingBottom: 0 }}
                    subheader={
                      <ListSubheader
                        style={{
                          fontFamily: "Plus Jakarta Sans",
                          paddingLeft: "16px",
                          lineHeight: '24px',
                          fontWeight: '700',
                          marginTop: 
                            section.subheader.includes('admin') ||
                            section.subheader.includes('Support')
                              ? "16px" 
                              : "8px",
                          marginBottom: "8px",
                          marginLeft: "12px",
                          color: 'var(--color-text)'
                        }}
                        disableGutters
                        disableSticky
                      >
                        {section.subheader.includes('admin') ||
                        section.subheader.includes('Support')
                          ? t(section.subheader)
                          : ''}
                      </ListSubheader>
                    }
                  >
                    {renderNavItems({
                      items: section.items,
                      pathname: location.pathname,
                      translation: t,
                      pendingRequestsLength: pendingRequestsCount,
                      activeAlertsCount: subAccountAlerts?.length ? subAccountAlerts.length : 0,
                    })}
                  </List>
                ))
              : null}
          </Box>
        )}
        <div style={{flex: 1}}></div>
        <MarketingBox />
      </PerfectScrollbar>
      <Dialog
        scroll={"paper"}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: '#242428',
            opacity: "0.7 !important"
          }
        }}
        classes={{
          scrollPaper: classes.searchPaletteContainer
        }}
        open={searchPaletteIsOpen}
        onClose={handleSearchPaletteClose}
        PaperProps={{
          className: classes.metalContent,
        }}
      >
        <DialogTitle sx={{padding: "16px 18px"}}>
          <div style={{display: "flex", color: "#9999A1"}}>
            <TextField
              onKeyDown={e => (e.key === 'ArrowDown' || e.key === "ArrowUp") && e.preventDefault()}
              autoFocus              
              fullWidth
              maxWidth={"md"}
              size="small"
              variant="standard"
              sx={{
                justifyContent: "center",
                "& .MuiInput-underline::before, .MuiInput-underline:after": {
                  borderBottom: "none !important"
                },
                "& .MuiInput-input": {
                  padding: 0
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon fontSize="small" color="action">
                      <SearchIcon color={"#9999A1"}/>
                    </SvgIcon>
                  </InputAdornment>
                )
              }}
              placeholder={t('search')}
              onChange={handleGlobalSearchChange}
            />
            <Box style={{padding: "2px 4px", borderRadius: "4px", border: "1px solid #242428"}}>
              <Typography>{os === "macOS" ? "⌘K" : "ctrl + k"}</Typography>
            </Box>
          </div>
          
        </DialogTitle>
        <Divider color={"#242428"} style={{marginBottom: "16px"}}/>
        {
          !sections.flatMap(i => i.items).filter(e=>{
            return e.title.toLowerCase().includes(globalSearchQuery)
          }).length && !searchSubAccounts.filter(e=>{
            return e.subAccountName.toLowerCase().includes(globalSearchQuery)
          }).length && <Typography className={classes.iconText} style={{color: "#FBFBFB", margin: "0 18px 16px"}}>No search results</Typography>
        }
        <DialogContent sx={{padding: "0", display: "flex", flexDirection: "column"}}>
          {/* Recent */}
          {
              !!globalSearchQuery.length
                ? <div style={{display: searchRecents.map(e => JSON.parse(e)).filter(e=>{
                  return e.value.toLowerCase().includes(globalSearchQuery)
                }).length ? "flex" : "none", flexDirection: "column", alignItems: "center", alignItems: "flex-start", margin: "0"}}>
                    <Typography className={classes.iconText} style={{color: "#9999A1", marginBottom: "4px", marginLeft: "18px"}}>Recent</Typography>
                    <List dense sx={{width: "100%", paddingTop: "0", paddingBottom: "0", marginTop: "8px", marginBottom: "12px"}}>
                      {
                        searchRecents.map(e => JSON.parse(e)).filter(e=>{
                          return e.value.toLowerCase().includes(globalSearchQuery)
                        }).map((e, index) =>{
                          if (index <3) {
                            const LucideIcon = icons[e.icon];
                            return (
                              <ListItem key={index} style={{paddingLeft: "0"}} ref={keyboardSelectIndex === "10" + index ? divRef : index === 0 ? topRef : null}>
                                <ListItemButton data-id={JSON.stringify({type: e.type, value: e.value, icon: e.icon, groupName: e.groupName, href: e.href})} onClick={handleSearchClick} sx={{padding: "2px 0", margin: 0, textDecoration: keyboardSelectIndex === "10"+index ? "none" : "inherit", backgroundColor: keyboardSelectIndex === "10"+index ? "#242428" : "inherit", marginLeft: keyboardSelectIndex === "10"+index ? "10px" : "18px", paddingLeft: keyboardSelectIndex === "10"+index ? "8px" : "inherit", borderRadius: keyboardSelectIndex === "10"+index ? "8px" : "inherit"}} component={RouterLink} to={e.type === "page" ? e.href : `/app/sub-accounts/${e.value}/dashboard`}>
                                  {
                                    e.icon && <ListItemIcon>
                                      <LucideIcon className={classes.icon} size={20}/>
                                    </ListItemIcon>
                                  }
                                  {
                                    e.type === "page" 
                                      ? <ListItemText
                                          className={classes.iconText}
                                          primary={t(e.value)}
                                        />
                                      : <ListItemText
                                          style={{display: "flex"}}
                                          className={classes.iconText}
                                          primary={e.value}
                                          secondary={e.groupName}
                                        />
                                  }
                                </ListItemButton>
                              </ListItem>
                            )
                          }
                        })
                      }
                    </List>
                  </div>
                : <div style={{display: searchRecents.length ? "flex" : "none", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", margin: "0"}}>
                    <Typography className={classes.iconText} style={{color: "#9999A1", marginBottom: "4px", marginLeft: "18px"}}>Recent</Typography>
                    <List dense sx={{width: "100%", paddingTop: "0", paddingBottom: "0", marginTop: "8px", marginBottom: "12px"}}>
                      {
                        searchRecents.map(e => JSON.parse(e)).filter(e=>{
                          return e.value.toLowerCase().includes(globalSearchQuery)
                        }).map((e, index) =>{
                          if (index <3) {
                            const LucideIcon = icons[e.icon];
                            return (
                              <ListItem key={index} style={{paddingLeft: "0"}} ref={keyboardSelectIndex === "10" + index ? divRef : index === 0 ? topRef : null}>
                                <ListItemButton data-id={JSON.stringify({type: e.type, value: e.value, icon: e.icon, groupName: e.groupName, href: e.href})} onClick={handleSearchClick} sx={{padding: "2px 0", margin: 0, textDecoration: keyboardSelectIndex === "10"+index ? "none" : "inherit", backgroundColor: keyboardSelectIndex === "10"+index ? "#242428" : "inherit", marginLeft: keyboardSelectIndex === "10"+index ? "10px" : "18px", paddingLeft: keyboardSelectIndex === "10"+index ? "8px" : "inherit", borderRadius: keyboardSelectIndex === "10"+index ? "8px" : "inherit"}} component={RouterLink} to={e.type === "page" ? e.href : `/app/sub-accounts/${e.value}/dashboard`}>
                                {
                                      e.icon && <ListItemIcon>
                                        <LucideIcon className={classes.icon} size={20}/>
                                      </ListItemIcon>
                                    }
                                    {
                                      e.type === "page" 
                                        ? <ListItemText
                                            className={classes.iconText}
                                            primary={t(e.value)}
                                          />
                                        : <ListItemText
                                            style={{display: "flex"}}
                                            className={classes.iconText}
                                            primary={e.value}
                                            secondary={e.groupName}
                                          />
                                    }
                                </ListItemButton>
                              </ListItem>
                            )
                          }
                        })
                      }
                  </List>
                </div>
            }
          {
            <Divider color={"#242428"} style={{display: searchRecents.map(e => JSON.parse(e)).filter(e=>{
              return e.value.toLowerCase().includes(globalSearchQuery)
            }).length ? "flex" : "none", marginBottom: "16px"}}/>
          }
          {/* Pages */}
            {
              !!globalSearchQuery
                ? <div style={{display: sections.flatMap(i => i.items).filter(e=>{
                  return e.title.toLowerCase().includes(globalSearchQuery)
                }).length ? "flex" : "none", flexDirection: "column", alignItems: "center", alignItems: "flex-start", margin: "0"}}>
                    <Typography className={classes.iconText} style={{color: "#9999A1", marginBottom: "4px", marginLeft: "18px"}}>Pages</Typography>
                    <List dense sx={{width: "100%", maxHeight: "150px", overflow:"auto", paddingTop: "0", paddingBottom: "0", marginTop: "8px", marginBottom: "8px"}}>
                      {
                        sections.flatMap(i => i.items).filter(e=>{
                          return e.title.toLowerCase().includes(globalSearchQuery)
                        }).map((e, index) =>{
                          const LucideIcon = icons[e.icon];  
                          return (
                            <ListItem key={e.title} style={{paddingLeft: "0"}} ref={keyboardSelectIndex === "20" + index ? divRef : index === 0 ? topRef : null}>
                              <ListItemButton data-id={JSON.stringify({type: "page", value: e.title, icon: e.icon, groupName: '', href: e.href})} onClick={handleSearchClick} sx={{padding: "2px 0", margin: 0, textDecoration: keyboardSelectIndex === "20"+index ? "none" : "inherit", backgroundColor: keyboardSelectIndex === "20"+index ? "#242428" : "inherit", marginLeft: keyboardSelectIndex === "20"+index ? "10px" : "18px", paddingLeft: keyboardSelectIndex === "20"+index ? "8px" : "inherit", borderRadius: keyboardSelectIndex === "20"+index ? "8px" : "inherit"}} component={RouterLink} to={e.href}>
                                <ListItemIcon>
                                  <LucideIcon className={classes.icon} size={20}/>
                                </ListItemIcon>
                                <ListItemText
                                  className={classes.iconText}
                                  primary={t(e.title)}
                                />
                              </ListItemButton>
                            </ListItem>
                          )
                        })
                      }
                    </List>
                  </div>
                : <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", margin: "0"}}>
                    <Typography className={classes.iconText} style={{color: "#9999A1", marginBottom: "4px", marginLeft: "18px"}}>Pages</Typography>
                    <List dense sx={{width: "100%", maxHeight: "150px", overflow:"auto", paddingTop: "0", paddingBottom: "0", marginTop: "8px", marginBottom: "8px"}}>
                      {sections.flatMap(i => i.items).map((e, index) =>{
                      if (index <3) {
                        const LucideIcon = icons[e.icon];
                        return (
                          <ListItem key={index} style={{paddingLeft: "0"}} ref={keyboardSelectIndex === "20" + index ? divRef : index === 0 ? topRef : null}>
                            <ListItemButton data-id={JSON.stringify({type: "page", value: e.title, icon: e.icon, groupName: '', href: e.href})} onClick={handleSearchClick} sx={{padding: "2px 0", margin: 0, textDecoration: keyboardSelectIndex === "20"+index ? "none" : "inherit", backgroundColor: keyboardSelectIndex === "20"+index ? "#242428" : "inherit", marginLeft: keyboardSelectIndex === "20"+index ? "10px" : "18px", paddingLeft: keyboardSelectIndex === "20"+index ? "8px" : "inherit", borderRadius: keyboardSelectIndex === "20"+index ? "8px" : "inherit"}} component={RouterLink} to={e.href}>
                              <ListItemIcon>
                                <LucideIcon className={classes.icon} size={20}/>
                              </ListItemIcon>
                              <ListItemText
                                className={classes.iconText}
                                primary={t(e.title)}
                              />
                            </ListItemButton>
                          </ListItem>
                        )
                      }
                    })}
                  </List>
                </div>
            }

          {/* Sub-Accounts */}
          {
              !!globalSearchQuery.length
                ? <div style={{display: searchSubAccounts.filter(e=>{
                  return e.subAccountName.toLowerCase().includes(globalSearchQuery)
                }).length ? "flex" : "none", flexDirection: "column", alignItems: "center", alignItems: "flex-start", margin: "0"}}>
                    <Typography className={classes.iconText} style={{color: "#9999A1", marginTop: "0", marginLeft: "18px"}}>Sub-Accounts</Typography>
                    <List dense sx={{width: "100%", maxHeight: "150px", overflow:"auto", paddingTop: "0", paddingBottom: "0", marginTop: "12px", marginBottom: "8px"}}>
                      {
                        searchSubAccounts.filter(e=>{
                          return e.subAccountName.toLowerCase().includes(globalSearchQuery)
                        }).map((e, index) =>{
                          return (
                            <ListItem key={e.subAccountName} style={{paddingLeft: "0"}} ref={keyboardSelectIndex === "30" + index ? divRef : index === 0 ? topRef : null}>
                              <ListItemButton data-id={JSON.stringify({type: "sub-accounts", value: e.subAccountName, icon: "", groupName: e.groupName, href: ''})} onClick={handleSearchClick} sx={{padding: "2px 0", margin: 0, textDecoration: keyboardSelectIndex === "30"+index ? "none" : "inherit", backgroundColor: keyboardSelectIndex === "30"+index ? "#242428" : "inherit", marginLeft: keyboardSelectIndex === "30"+index ? "10px" : "18px", paddingLeft: keyboardSelectIndex === "30"+index ? "8px" : "inherit", borderRadius: keyboardSelectIndex === "30"+index ? "8px" : "inherit"}} component={RouterLink} to={`/app/sub-accounts/${e.subAccountName}/dashboard`}>
                                <ListItemText
                                  style={{display: "flex"}}
                                  className={classes.iconText}
                                  primary={e.subAccountName}
                                  secondary={e.groupName}
                                />
                              </ListItemButton>
                            </ListItem>
                          )
                        })
                      }
                    </List>
                  </div>
                : <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", margin: "0", overflow:"auto", maxHeight: "200px"}}>
                    <Typography className={classes.iconText} style={{color: "#9999A1", marginTop: "0", marginLeft: "18px"}}>Sub-Accounts</Typography>
                    {
                      searchSubAccounts.length
                        ? <List dense sx={{width: "100%", maxHeight: "150px", overflow:"auto", paddingTop: "0", paddingBottom: "0", marginTop: "12px", marginBottom: "8px"}}>
                          {
                            searchSubAccounts.map((e, index) =>{
                              return (
                                <ListItem key={index} style={{paddingLeft: "0"}} ref={keyboardSelectIndex === "30" + index ? divRef : index === 0 ? topRef : null}>
                                  <ListItemButton data-id={JSON.stringify({type: "sub-account", value: e.subAccountName, icon: '', groupName: e.groupName, href: ''})} onClick={handleSearchClick} sx={{padding: "2px 0", margin: 0, textDecoration: keyboardSelectIndex === "30"+index ? "none" : "inherit", backgroundColor: keyboardSelectIndex === "30"+index ? "#242428" : "inherit", marginLeft: keyboardSelectIndex === "30"+index ? "10px" : "18px", paddingLeft: keyboardSelectIndex === "30"+index ? "8px" : "inherit", borderRadius: keyboardSelectIndex === "30"+index ? "8px" : "inherit"}} component={RouterLink} to={`/app/sub-accounts/${e.subAccountName}/dashboard`}>
                                    <ListItemText
                                      style={{display: "flex"}}
                                      className={classes.iconText}
                                      primary={e.subAccountName}
                                      secondary={e.groupName}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )
                            })
                          }
                      </List>
                      : <Table>
                          <TableBody>
                            <TableRow >
                              <TableCell sx={{paddingLeft: "18px"}} align="left" aligncomponent="th">
                                <div style={{display: "flex"}}>
                                  <Skeleton variant="rounded" style={{maxWidth: "300px" }} width={"80px"} height={24} sx={{ bgcolor: '#FBFBFB' }}/>
                                  <Skeleton variant="rounded" style={{maxWidth: "300px", marginLeft: "8px" }} width={"120px"} height={24} sx={{ bgcolor: '#adb0bb' }}/>
                                </div>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                  }
                </div>
            }
        </DialogContent>
        <Divider color={"#242428"} />
        <DialogActions sx={{display:"flex", justifyContent: "flex-start", backgroundColor: "rgba(12, 17, 29, 0.8)", padding: "8px 18px"}}>
          <Button sx={{backgroundColor: "#141416", borderRadius: "8px", padding: "5px", border: "1px solid #242428", height: "28px", width: "28px", minWidth: "unset"}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="#9999A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
          <Button sx={{backgroundColor: "#141416", borderRadius: "8px", padding: "5px", border: "1px solid #242428", height: "28px", width: "28px", minWidth: "unset"}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19M12 19L19 12M12 19L5 12" stroke="#9999A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
          <Typography sx={{color: "rgba(153, 153, 161, 1)", fontSize: "14px"}}>to navigate</Typography>
          <Button sx={{marginLeft: "12px !important", backgroundColor: "#141416", borderRadius: "8px", padding: "5px", border: "1px solid #242428", height: "28px", width: "28px", minWidth: "unset"}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 4V5.4C20 8.76031 20 10.4405 19.346 11.7239C18.7708 12.8529 17.8529 13.7708 16.7239 14.346C15.4405 15 13.7603 15 10.4 15H4M4 15L9 10M4 15L9 20" stroke="#9999A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
          <Typography sx={{color: "rgba(153, 153, 161, 1)", fontSize: "14px"}}>to click</Typography>
          <Button sx={{marginLeft: "12px !important", backgroundColor: "#141416", borderRadius: "8px", padding: "5px", border: "1px solid #242428", height: "28px", width: "28px", minWidth: "unset"}}>
            <Typography sx={{textTransform:"lowercase", color: "rgba(153, 153, 161, 1)", fontSize: "12px"}}>esc</Typography>
          </Button>
          <Typography sx={{color: "rgba(153, 153, 161, 1)", fontSize: "14px"}}>to close</Typography>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <>
      {!mobileDevice ? (
        <Drawer
          anchor="left"
          classes={{ paper: classes.metalDesktop }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      ) : (
        <Drawer
          anchor="left"
          classes={{
            paper: classes.metalMobileDrawer
          }}
          onClose={() => handleSideNavUpdate(false)}
          open={navOpen}
          variant="temporary"
        >
          {content}
        </Drawer>
      )}
    </>
  );
};

export default NavBar;
